import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Loader2, LogOut } from "lucide-react";

export default function ForcePasswordChange() {
  const { changePassword, logout, user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setError("Passwords do not match."); return; }
    setSaving(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated. Welcome aboard!");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to change password");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm fade-up">
        <div className="flex items-center gap-2 mb-6">
          <img src="/logo.png" alt="GirOrganic" className="h-10 w-10 rounded-full object-cover" />
          <div>
            <p className="font-heading font-extrabold text-lg text-[#111411] leading-none">Set a new password</p>
            <p className="text-xs text-muted-foreground mt-0.5">{user?.email}</p>
          </div>
        </div>
        <div className="rounded-lg border border-border bg-white p-6">
          <div className="flex items-center gap-2 text-[#0B3D3D] mb-3">
            <KeyRound className="h-5 w-5" />
            <h2 className="font-heading font-bold text-[#111411]">First-time login</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            For security, please replace your temporary password before continuing.
          </p>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Temporary password</Label>
              <Input type="password" data-testid="fpc-current" value={current} onChange={(e) => setCurrent(e.target.value)} required className="mt-1.5" />
            </div>
            <div>
              <Label>New password</Label>
              <Input type="password" data-testid="fpc-new" value={next} onChange={(e) => setNext(e.target.value)} required className="mt-1.5" />
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input type="password" data-testid="fpc-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5" />
            </div>
            {error && <p className="text-sm text-[#C24127]" data-testid="fpc-error">{error}</p>}
            <Button type="submit" disabled={saving} data-testid="fpc-submit" className="w-full bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update password & continue
            </Button>
          </form>
          <Button variant="ghost" size="sm" onClick={logout} className="w-full mt-2 gap-2 text-muted-foreground">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
